import { useObjectUrlCacheManager } from 'hooks/objectUrl'
import { forwardRef, type ForwardedRef, useEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import { useRepository } from 'hooks/auth'
import { observer } from 'mobx-react-lite'
import { StorageFetchError } from '@breakoutlearning/firebase-repository/types'
import {
  canSendFirebaseToken,
  emulatorStorageOrigins,
  isSignedUrl,
} from 'util/storageUrl'

type GoogleStorageVideoProps = {
  src?: string
  vttSrc?: string
  vttLabel?: string
  alt: string
  loader?: React.ReactNode
  namespace: string
  skipCache?: boolean
  decoding?: 'sync' | 'async' | 'auto'
  disablePictureInPicture?: boolean
  disableRemotePlayback?: boolean
  children?: React.ReactNode
} & React.MediaHTMLAttributes<HTMLVideoElement>

function useAuthenticatedBlobUrl(
  src: string | undefined,
  namespace: string,
  skipCache?: boolean
) {
  const { authToken } = useRepository()
  const objectUrlCache = useObjectUrlCacheManager()
  const initialObjectUrl = skipCache
    ? undefined
    : objectUrlCache.get(namespace, src)
  const [objectUrl, setObjectUrl] = useState<string | undefined>(
    initialObjectUrl
  )
  const [retries, setRetries] = useState(0)

  useEffect(() => {
    if (!src) return
    if (objectUrl) return
    if (retries >= 3) return

    // Signed URLs authenticate via the signature in the query string. Adding an
    // Authorization header makes the request non-simple and triggers a CORS
    // preflight the storage bucket rejects. Only attach the bearer token for
    // unsigned Firebase Storage URLs: any other host could read it.
    const shouldSendToken =
      !isSignedUrl(src) && canSendFirebaseToken(src, emulatorStorageOrigins)
    if (shouldSendToken && !authToken) return

    const options = shouldSendToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : undefined

    setTimeout(
      () => {
        fetch(src, options)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            return res.blob()
          })
          .then((blob) => {
            const newObjectURL = URL.createObjectURL(blob)
            if (!skipCache) {
              objectUrlCache.set(namespace, src, newObjectURL)
            }
            setObjectUrl(newObjectURL)
          })
          .catch((err) => {
            if (retries < 3) {
              setRetries(retries + 1)
            } else {
              captureException(
                new StorageFetchError(`Failed to fetch storage asset: ${src}`, {
                  cause: err,
                })
              )
              console.error('Failed to fetch asset', err)
            }
          })
      },
      1000 * retries * 2 // exponential backoff - 0s, 2s, 4s, 8s
    )
  }, [authToken, src, objectUrlCache, namespace, skipCache, objectUrl, retries])

  return objectUrl
}

const GoogleStorageNoObserver = forwardRef(function GoogleStorageVideo(
  {
    src,
    vttSrc,
    vttLabel,
    namespace,
    skipCache,
    loader,
    children,
    ...rest
  }: GoogleStorageVideoProps,
  ref: ForwardedRef<HTMLVideoElement>
) {
  const objectUrl = useAuthenticatedBlobUrl(src, namespace, skipCache)
  const vttObjectUrl = useAuthenticatedBlobUrl(
    vttSrc,
    `${namespace}-vtt`,
    skipCache
  )

  if (!objectUrl) return loader || null

  // Both branches render a <video> in the same position, so React keeps the
  // same element (and playback) when the caption file finishes loading.
  if (!vttObjectUrl) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- no caption file exists (or it has not loaded yet) for this video
      <video ref={ref} src={objectUrl} {...rest}>
        {children}
      </video>
    )
  }

  return (
    <video ref={ref} src={objectUrl} {...rest}>
      {/* captions, not subtitles: the VTT is a same-language speech
          transcript (golang/server/cmd/video_captions) and is the only text
          alternative for viewers who can't hear the audio. It has no sound-effect
          cues yet. */}
      <track
        kind="captions"
        srcLang="en"
        label={vttLabel ?? 'English'}
        src={vttObjectUrl}
      />
      {children}
    </video>
  )
})

export const GoogleStorageVideo = observer(function GoogleStorageVideo(
  props: GoogleStorageVideoProps & { ref?: ForwardedRef<HTMLVideoElement> }
) {
  return <GoogleStorageNoObserver {...props} ref={props.ref} />
})
